<script setup>
import { computed } from 'vue';
import PdSpinner from './PdSpinner.vue';

const props = defineProps({
  /** primary (orange), ink, neutral (light grey fill, also accepted as outline), soft, ghost, link, danger */
  variant: { type: String, default: 'primary' },
  /** sm, md, lg */
  size: { type: String, default: 'md' },
  href: { type: String, default: null },
  type: { type: String, default: 'button' },
  iconLeft: { type: [Object, Function], default: null },
  iconRight: { type: [Object, Function], default: null },
  loading: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
  block: { type: Boolean, default: false },
  /** Stretches the label to the left and pushes the right icon to the far edge */
  spread: { type: Boolean, default: false },
});

const tag = computed(() => (props.href ? 'a' : 'button'));
</script>

<template>
  <component
    :is="tag"
    :href="href || undefined"
    :type="href ? undefined : type"
    :disabled="href ? undefined : disabled || loading"
    :aria-disabled="disabled || loading || undefined"
    :aria-busy="loading || undefined"
    class="pd-btn"
    :class="[`pd-btn--${variant === 'outline' ? 'neutral' : variant}`, `pd-btn--${size}`, { 'is-block': block, 'is-spread': spread, 'is-loading': loading }]"
  >
    <PdSpinner v-if="loading" size="sm" class="pd-btn__spin" />
    <component :is="iconLeft" v-else-if="iconLeft" class="pd-btn__icon" :size="18" :stroke-width="2.25" aria-hidden="true" />
    <span class="pd-btn__label"><slot /></span>
    <component :is="iconRight" v-if="iconRight" class="pd-btn__icon" :size="18" :stroke-width="2.25" aria-hidden="true" />
  </component>
</template>

<style scoped>
.pd-btn {
  --h: 48px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  height: var(--h);
  padding: 0 22px;
  border: 1.5px solid transparent;
  border-radius: var(--pd-r-pill);
  font-weight: 800;
  font-size: 0.9375rem;
  letter-spacing: -0.01em;
  text-decoration: none;
  white-space: nowrap;
  cursor: pointer;
  user-select: none;
  transition: background-color var(--pd-fast) var(--pd-ease), border-color var(--pd-fast) var(--pd-ease),
    color var(--pd-fast) var(--pd-ease), transform var(--pd-fast) var(--pd-ease);
}
.pd-btn:active:not(:disabled) { transform: scale(0.97); }
.pd-btn--sm { --h: 36px; padding: 0 14px; font-size: 0.8125rem; gap: 8px; }
.pd-btn--lg { --h: 58px; padding: 0 28px; font-size: 1rem; }
.is-block { display: flex; width: 100%; }
.is-spread { justify-content: space-between; }
.is-spread .pd-btn__label { flex: 1; text-align: left; }

.pd-btn--primary { background: var(--pd-orange); color: var(--pd-on-orange); }
.pd-btn--ink { background: var(--pd-ink); color: var(--pd-white); }
.pd-btn--neutral { background: var(--pd-neutral); color: var(--pd-ink) !important; }  /* always black text, whatever surface it sits on */
.pd-btn--soft { background: var(--pd-tint); color: var(--pd-orange-text); }
.pd-btn--ghost { background: transparent; color: var(--pd-ink); }
.pd-btn--danger { background: var(--pd-danger); color: var(--pd-white); }
.pd-btn--link {
  --h: auto;
  padding: 0 0 4px;
  border: 0;
  border-bottom: 2px solid currentColor;
  border-radius: 0;
  background: transparent;
  color: var(--pd-orange-text);
}
.pd-btn--link:active:not(:disabled) { transform: none; }

@media (hover: hover) {
  .pd-btn--primary:hover:not(:disabled) { background: var(--pd-orange-hover); }
  .pd-btn--ink:hover:not(:disabled) { background: var(--pd-ink-3); }
  .pd-btn--neutral:hover:not(:disabled) { background: var(--pd-neutral-hover); }
  .pd-btn--soft:hover:not(:disabled) { background: var(--pd-peach); }
  .pd-btn--ghost:hover:not(:disabled) { background: var(--pd-sand); }
  .pd-btn--link:hover { color: var(--pd-ink); }
}

.pd-btn:disabled,
.pd-btn[aria-disabled='true'] { cursor: not-allowed; }
.pd-btn--primary:disabled { background: var(--pd-peach); color: rgba(18, 18, 18, 0.45); }
.pd-btn--ink:disabled { background: var(--pd-line-strong); color: var(--pd-white); }
.pd-btn--neutral:disabled,
.pd-btn--ghost:disabled { color: var(--pd-subtle); }
.is-loading { cursor: progress; }
</style>
